import React, { useState, useMemo } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, ArrowLeft, Search, Inbox, User as UserIcon, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import SmsConversationTimeline from '@/components/admin/SmsConversationTimeline';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// The email and SMS inboxes are one destination in the sidebar, joined by this
// strip. Each keeps its own route, data and actions.
const INBOX_TABS = [
  { to: '/admin/email-inbox', label: 'EMAIL INBOX' },
  { to: '/admin/sms-inbox', label: 'SMS INBOX' },
];

function normalizePhone(raw) {
  if (!raw) return null;
  let s = String(raw).trim().replace(/[\s\-().]/g, '');
  if (!s) return null;
  if (s.startsWith('+44')) s = '+44' + s.slice(3);
  else if (s.startsWith('44') && s.length >= 11) s = '+' + s;
  else if (s.startsWith('0')) s = '+44' + s.slice(1);
  else if (/^7\d{9}$/.test(s)) s = '+44' + s;
  else return null;
  if (!/^\+44\d{9,10}$/.test(s)) return null;
  return s;
}

export default function AdminSmsInbox() {
  const { user: currentUser, isLoadingAuth } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('unread'); // 'unread' | 'all'
  const [selectedPhone, setSelectedPhone] = useState(null);

  const { data: allMessages = [], isLoading } = useQuery({
    queryKey: ['admin-sms-inbox'],
    queryFn: () => base44.entities.SmsMessage.list('-created_date', 500),
    enabled: currentUser?.role === 'admin',
  });

  // Group inbound messages by phone number; keep the latest inbound per conversation.
  const conversations = useMemo(() => {
    const inbound = allMessages.filter((m) => m.direction === 'inbound');
    const byPhone = {};
    for (const m of inbound) {
      const key = m.phone_number;
      if (!byPhone[key] || new Date(m.created_date) > new Date(byPhone[key].created_date)) {
        byPhone[key] = m;
      }
    }
    let list = Object.values(byPhone);
    list.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

    // Apply filters
    const q = search.toLowerCase();
    list = list.filter((c) => {
      const matchesSearch = !q ||
        (c.phone_number || '').toLowerCase().includes(q) ||
        (c.customer_name || '').toLowerCase().includes(q);
      const hasUnread = allMessages.some(
        (m) => m.phone_number === c.phone_number && m.direction === 'inbound' && m.status === 'new'
      );
      const matchesFilter = filter === 'all' || hasUnread;
      return matchesSearch && matchesFilter;
    });

    return list;
  }, [allMessages, search, filter]);

  const unreadCount = useMemo(() => {
    const phones = new Set();
    for (const m of allMessages) {
      if (m.direction === 'inbound' && m.status === 'new') phones.add(m.phone_number);
    }
    return phones.size;
  }, [allMessages]);

  const handleSelect = async (phone) => {
    setSelectedPhone(phone);
    // Mark all inbound messages for this phone as read
    try {
      await base44.entities.SmsMessage.updateMany(
        { phone_number: phone, direction: 'inbound', status: 'new' },
        { $set: { status: 'read' } }
      );
      queryClient.invalidateQueries({ queryKey: ['admin-sms-inbox'] });
    } catch (e) {
      console.error('Failed to mark read:', e);
    }
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const selectedConversation = conversations.find((c) => c.phone_number === selectedPhone) ||
    allMessages.find((m) => m.phone_number === selectedPhone);

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">ORDERS &amp; CUSTOMERS</p>
            <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">SMS INBOX</h1>
            <p className="text-white/30 text-sm mt-1">
              {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-16 max-w-6xl mx-auto">
        <AdminSectionTabs tabs={INBOX_TABS} className="mb-6" />
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or number..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <div className="flex gap-1 border border-white/10">
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2.5 text-[10px] font-bold tracking-widest transition-colors ${filter === 'unread' ? 'bg-primary text-white' : 'text-white/40 hover:text-white'}`}
            >
              UNREAD{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2.5 text-[10px] font-bold tracking-widest transition-colors ${filter === 'all' ? 'bg-primary text-white' : 'text-white/40 hover:text-white'}`}
            >
              ALL
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Inbox className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No SMS replies {filter === 'unread' ? 'unread' : 'found'}.</p>
            <p className="text-white/20 text-xs mt-1">Inbound customer replies will appear here once the Twilio webhook is configured.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Conversation list */}
            <div className={`lg:col-span-2 space-y-2 ${selectedPhone ? 'hidden lg:block' : 'block'}`}>
              {conversations.map((c) => {
                const hasUnread = allMessages.some(
                  (m) => m.phone_number === c.phone_number && m.direction === 'inbound' && m.status === 'new'
                );
                const isSelected = selectedPhone === c.phone_number;
                return (
                  <button
                    key={c.phone_number}
                    onClick={() => handleSelect(c.phone_number)}
                    className={`w-full text-left border px-4 py-3 transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-white/5 bg-[#111115] hover:border-white/15'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-bold truncate">
                          {c.customer_name || 'Unknown sender'}
                        </p>
                        <p className="text-white/30 text-xs truncate">{c.phone_number}</p>
                      </div>
                      {hasUnread && (
                        <span className="bg-primary text-white text-[8px] font-bold tracking-widest px-1.5 py-0.5 shrink-0">
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs line-clamp-2 mb-1">{c.body}</p>
                    <p className="text-white/20 text-[9px]">
                      {c.created_date ? format(new Date(c.created_date), 'dd MMM yyyy · HH:mm') : ''}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Thread panel */}
            {selectedPhone && (
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white font-bold text-sm">
                      {selectedConversation?.customer_name || 'Unknown sender'}
                    </p>
                    <p className="text-white/30 text-xs">{selectedPhone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConversation?.customer_id && (
                      <Link
                        to="/admin/crm/directory"
                        className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-widest border border-white/10 px-3 py-1.5 transition-colors"
                      >
                        <UserIcon className="w-3 h-3" /> CUSTOMER
                      </Link>
                    )}
                    <button
                      onClick={() => setSelectedPhone(null)}
                      className="lg:hidden text-white/40 hover:text-white text-[10px] font-bold tracking-widest border border-white/10 px-3 py-1.5 transition-colors"
                    >
                      BACK
                    </button>
                  </div>
                </div>
                <SmsConversationTimeline
                  phone={selectedPhone}
                  customerName={selectedConversation?.customer_name}
                />
              </div>
            )}

            {/* Empty state when no conversation selected (desktop) */}
            {!selectedPhone && conversations.length > 0 && (
              <div className="hidden lg:flex lg:col-span-3 items-center justify-center border border-white/5 bg-[#111115] min-h-[400px]">
                <div className="text-center">
                  <Inbox className="w-8 h-8 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 text-xs">Select a conversation to view the thread</p>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}